import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { usePageMeta } from '../lib/usePageMeta';

const POST = {
  slug: 'how-to-publish-a-book-on-amazon-kindle-2026',
  title: 'How to Publish a Book on Amazon Kindle in 2026: The Complete Step-by-Step Guide',
  description:
    'A practical, no-fluff guide to publishing your first novel on Amazon Kindle Direct Publishing (KDP). Covers formatting, cover design, pricing, royalties, KDP Select vs. wide, and the launch-week tactics that move books in 2026.',
  author: 'StoryMint Team',
  authorRole: 'StoryMint Editorial',
  category: 'Publishing',
  publishedISO: '2026-05-08T09:00:00Z',
  modifiedISO: '2026-05-11T09:00:00Z',
  publishedLabel: 'May 8, 2026',
  readingTime: '12 min read',
  url: 'https://storymint.io/blog/how-to-publish-a-book-on-amazon-kindle-2026',
  cover: '/og/kindle-publishing-guide.png',
  tags: [
    'Amazon KDP',
    'Self Publishing',
    'Kindle Direct Publishing',
    'Indie Author',
    'Ebook Publishing',
    'KDP Select',
  ],
};

const TOC = [
  { id: 'why-kdp', label: '1. Why Amazon Kindle is still the default in 2026' },
  { id: 'prepare-manuscript', label: '2. Preparing your manuscript' },
  { id: 'cover-design', label: '3. Cover design that converts' },
  { id: 'kdp-account', label: '4. Setting up your KDP account' },
  { id: 'metadata', label: '5. Title, subtitle, and metadata' },
  { id: 'categories-keywords', label: '6. Categories and keywords' },
  { id: 'pricing-royalties', label: '7. Pricing and royalties' },
  { id: 'kdp-select', label: '8. KDP Select vs. going wide' },
  { id: 'launch-week', label: '9. Launch-week strategy' },
  { id: 'after-launch', label: '10. After launch: reviews, ads, and growth' },
  { id: 'faq', label: 'FAQ' },
];

/**
 * Legacy hardcoded test article - predates the file+Firestore registry system.
 * Kept render-as-today during the rollout per `documents/architecture/blog-system.md`.
 * BlogPost.tsx (the dispatcher) short-circuits to this when the slug matches
 * `LEGACY_SLUG` and no registry/Firestore record exists. Remove this file once
 * the equivalent post has been re-authored through the new system.
 */
export function BlogPostLegacy() {
  usePageMeta({
    title: `${POST.title} | StoryMint Blog`,
    description: POST.description,
    canonical: POST.url,
    ogType: 'article',
    ogImage: POST.cover,
    twitterCard: 'summary_large_image',
    articlePublishedTime: POST.publishedISO,
    articleModifiedTime: POST.modifiedISO,
    articleAuthor: POST.author,
    articleSection: POST.category,
    articleTags: POST.tags,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: POST.title,
      description: POST.description,
      image: [POST.cover],
      datePublished: POST.publishedISO,
      dateModified: POST.modifiedISO,
      author: {
        '@type': 'Person',
        name: POST.author,
        jobTitle: POST.authorRole,
      },
      publisher: {
        '@type': 'Organization',
        name: 'StoryMint',
        logo: {
          '@type': 'ImageObject',
          url: 'https://storymint.io/logo.png',
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': POST.url,
      },
      articleSection: POST.category,
      keywords: POST.tags.join(', '),
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <Navbar variant="public" />

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16" itemScope itemType="https://schema.org/Article">
        {/* Breadcrumbs (BreadcrumbList microdata) */}
        <nav
          aria-label="Breadcrumb"
          className="mb-8 text-sm text-muted-foreground"
          itemScope
          itemType="https://schema.org/BreadcrumbList"
        >
          <ol className="flex flex-wrap items-center gap-2">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" itemProp="item" className="hover:text-foreground transition-colors">
                <span itemProp="name">Home</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <li aria-hidden="true">/</li>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/blog" itemProp="item" className="hover:text-foreground transition-colors">
                <span itemProp="name">Blog</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <li aria-hidden="true">/</li>
            <li
              itemProp="itemListElement"
              itemScope
              itemType="https://schema.org/ListItem"
              className="text-foreground truncate max-w-[60vw]"
            >
              <span itemProp="name">Publishing a Book on Kindle</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        <header className="mb-10">
          <div className="text-xs font-bold text-primary tracking-[0.12em] uppercase mb-3">
            <span itemProp="articleSection">{POST.category}</span>
          </div>
          <h1
            itemProp="headline"
            className="text-[clamp(2rem,5vw,3.25rem)] font-extrabold text-foreground mb-6 leading-[1.15] tracking-tight"
          >
            {POST.title}
          </h1>
          <p className="text-base sm:text-xl text-muted-foreground leading-[1.65] mb-6" itemProp="description">
            {POST.description}
          </p>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <address
              className="not-italic"
              itemProp="author"
              itemScope
              itemType="https://schema.org/Person"
            >
              By <span itemProp="name" className="text-foreground font-medium">{POST.author}</span>
              <span className="hidden sm:inline"> · </span>
              <span className="block sm:inline" itemProp="jobTitle">{POST.authorRole}</span>
            </address>
            <span aria-hidden="true">·</span>
            <time dateTime={POST.publishedISO} itemProp="datePublished">
              {POST.publishedLabel}
            </time>
            <meta itemProp="dateModified" content={POST.modifiedISO} />
            <span aria-hidden="true">·</span>
            <span>{POST.readingTime}</span>
          </div>
        </header>

        {/* Cover */}
        <div
          className="rounded-xl border border-border overflow-hidden mb-10 h-64 sm:h-80 flex items-center justify-center text-7xl"
          style={{ background: 'linear-gradient(135deg, #0d0d12 0%, #1a1a2e 100%)' }}
          role="img"
          aria-label="Illustration of an open book transforming into a Kindle ereader screen"
        >
          <span aria-hidden="true">📚</span>
          <meta itemProp="image" content={POST.cover} />
        </div>

        {/* TL;DR */}
        <aside className="p-6 rounded-xl border border-border bg-card mb-10">
          <h2 className="text-sm font-bold text-primary tracking-[0.12em] uppercase mb-3">TL;DR</h2>
          <p className="text-muted-foreground text-sm leading-[1.7]">
            Publishing a book on Amazon Kindle in 2026 takes about a week if you already have a manuscript. The
            real work is the metadata - title, subtitle, categories, keywords, and pricing decisions that
            determine whether anyone ever finds your book. This guide walks through every screen of KDP, the
            decisions that actually matter, and the launch-week moves that move books past page 50 of the
            charts.
          </p>
        </aside>

        {/* Table of contents */}
        <nav aria-label="Table of contents" className="p-6 rounded-xl border border-border bg-card mb-12">
          <h2 className="text-sm font-bold text-foreground tracking-wide uppercase mb-4">Table of Contents</h2>
          <ol className="space-y-2 text-sm">
            {TOC.map((entry) => (
              <li key={entry.id}>
                <a
                  href={`#${entry.id}`}
                  className="text-muted-foreground hover:text-primary transition-colors"
                >
                  {entry.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        <div
          className="prose-content space-y-6 text-foreground text-[17px] leading-[1.8]"
          itemProp="articleBody"
        >
          <section id="why-kdp">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              1. Why Amazon Kindle is still the default in 2026
            </h2>
            <p className="text-muted-foreground">
              Amazon controls roughly 67% of the US ebook market and 80% of indie self-publishing revenue. Even
              if you plan to go wide eventually, Kindle Direct Publishing is where the gravity is - the place
              your readers default to when they want to buy your book. The flywheel is well-understood: more
              reviews lift visibility, visibility lifts sales, sales unlock the also-bought algorithm, and
              also-boughts feed more reviews.
            </p>
            <p className="text-muted-foreground">
              The platform has not radically changed since 2024, but a few important things have. The KDP
              dashboard now surfaces A+ Content analytics, the categories list was overhauled in early 2026
              (some legacy categories were retired), and Amazon Ads quietly raised the floor on relevance
              scoring. None of this changes the publishing flow - it just changes what wins.
            </p>
          </section>

          <section id="prepare-manuscript">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              2. Preparing your manuscript
            </h2>
            <p className="text-muted-foreground">
              Amazon accepts DOCX, EPUB, and KPF files. EPUB gives you the most predictable result - DOCX
              uploads occasionally introduce phantom page breaks, and KPF is only worth bothering with if you
              use Kindle Create. Whichever you choose, three things determine whether your manuscript looks
              professional on a Kindle:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">Use real heading styles.</strong> Chapter titles should
                live in Heading 1 (or Heading 2 if you have a part-and-chapter structure), not bold-and-bigger
                paragraph text. This is what generates the navigable table of contents inside the Kindle app.
              </li>
              <li>
                <strong className="text-foreground">Drop one indent style and stick to it.</strong> Either
                first-line indented paragraphs <em>or</em> block paragraphs with vertical space. Mixed
                formatting is the #1 amateur-publishing tell.
              </li>
              <li>
                <strong className="text-foreground">Embed your scene breaks.</strong> Three centered asterisks
                survive ereader font resizing - three blank lines do not.
              </li>
            </ul>
            <p className="text-muted-foreground">
              If you generated your novel inside StoryMint, the EPUB export already handles all three of these.
              Otherwise, run the file through{' '}
              <a
                href="https://kindlepreviewer.amazon.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-primary hover:underline"
              >
                Kindle Previewer
              </a>{' '}
              before uploading - it catches roughly 90% of formatting issues that would otherwise show up in
              your one-star reviews.
            </p>
          </section>

          <section id="cover-design">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              3. Cover design that converts
            </h2>
            <p className="text-muted-foreground">
              Your cover is doing one job: convincing a stranger scrolling a thumbnail grid that your book is
              in the genre they want to read. Not <em>your</em> genre. <em>Their</em> genre. The difference is
              what kills 90% of indie covers.
            </p>
            <p className="text-muted-foreground">
              Open the bestseller page for your subgenre. Look at the top 20 covers as thumbnails. Squint until
              the details blur. You should be able to instantly tell which subgenre each cover targets - dark
              romance vs. paranormal romance, cozy mystery vs. thriller, sword-and-sorcery vs. progression
              fantasy. Your cover needs to read at thumbnail size the same way.
            </p>
            <p className="text-muted-foreground">
              Resolution: 2,560 × 1,600 px (1.6:1) is the current sweet spot. Save as JPEG, under 50 MB. RGB
              colour space, not CMYK.
            </p>
          </section>

          <section id="kdp-account">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              4. Setting up your KDP account
            </h2>
            <p className="text-muted-foreground">
              Go to{' '}
              <a
                href="https://kdp.amazon.com"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-primary hover:underline"
              >
                kdp.amazon.com
              </a>{' '}
              and sign in with your regular Amazon account - Amazon strongly recommends not creating a separate
              one, because royalty disbursement and tax reporting are tied to the account itself.
            </p>
            <p className="text-muted-foreground">
              You will be asked for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>A tax interview (the W-9 for US authors, W-8BEN for non-US - about 10 minutes)</li>
              <li>Banking details for royalty deposits (direct deposit is faster than cheque by ~30 days)</li>
              <li>A payee name - this can be your real name, a sole-proprietorship, or an LLC</li>
            </ul>
            <p className="text-muted-foreground">
              Pen names are configured per-book on the metadata screen, not on the account. You can publish
              under unlimited pen names from a single KDP account. Many full-time indie authors maintain three
              or more pen names targeting different genres.
            </p>
          </section>

          <section id="metadata">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              5. Title, subtitle, and metadata
            </h2>
            <p className="text-muted-foreground">
              This is the part most authors rush and later regret. The title field on Kindle is not just a
              title - it is the single most-weighted signal in Amazon&apos;s ranking algorithm. You cannot
              stuff keywords (Amazon will deindex you), but you can be deliberate about subtitle.
            </p>
            <p className="text-muted-foreground">
              Compare a vague subtitle (&ldquo;A Novel&rdquo;) with a discoverable one (&ldquo;A
              Slow-Burn Enemies-to-Lovers Romance&rdquo;). The second tells the reader exactly what they are
              about to read <em>and</em> tells Amazon what shelves to put you on. Both matter.
            </p>
            <p className="text-muted-foreground">
              For description text, write your first paragraph as the hook a reader sees above the
              &ldquo;Read more&rdquo; fold on mobile - this is roughly 200 characters. Anything below that fold
              is for converting the curious, not capturing them.
            </p>
          </section>

          <section id="categories-keywords">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              6. Categories and keywords
            </h2>
            <p className="text-muted-foreground">
              Amazon gives you three category selections and seven keyword slots. The categories control which
              bestseller charts you can rank on. The keywords control which search terms surface your book.
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">For categories:</strong> pick the smallest, most specific
              subcategory you legitimately fit. &ldquo;Romance&rdquo; has 400,000+ titles competing.
              &ldquo;Romance &gt; Multicultural &gt; Asian American&rdquo; might have 1,200. You can rank #1 in
              the second one in your first week - and a #1 bestseller tag, even in a small category, is real
              social proof.
            </p>
            <p className="text-muted-foreground">
              <strong className="text-foreground">For keywords:</strong> think about complete search phrases,
              not single words. Real readers type things like &ldquo;clean small town romance new adult&rdquo;
              or &ldquo;cozy mystery cat sleuth&rdquo;. Use all seven slots. Avoid keywords already in your
              title or category - Amazon already indexes those.
            </p>
          </section>

          <section id="pricing-royalties">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              7. Pricing and royalties
            </h2>
            <p className="text-muted-foreground">
              KDP gives you two royalty options:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">35% royalty</strong> at any price from $0.99 to $200, no
                delivery fee.
              </li>
              <li>
                <strong className="text-foreground">70% royalty</strong> at prices between $2.99 and $9.99, in
                eligible countries, minus a per-MB delivery fee.
              </li>
            </ul>
            <p className="text-muted-foreground">
              The cliff at $2.99 is dramatic. A book priced at $2.98 earns about $1.04 per copy. The same book
              at $2.99 earns about $2.05. Doubling your earnings for one extra cent is rarely a hard decision.
            </p>
            <p className="text-muted-foreground">
              For your debut, $2.99 (book one of a series) or $3.99 (standalone) are the most common
              defensible price points in fiction. Discounts to $0.99 work well for launches and BookBub
              features, but only as a temporary tactic, not your default price.
            </p>
          </section>

          <section id="kdp-select">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              8. KDP Select vs. going wide
            </h2>
            <p className="text-muted-foreground">
              KDP Select is Amazon&apos;s exclusivity program. You give Amazon 90 days of ebook exclusivity in
              exchange for:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Inclusion in Kindle Unlimited (you get paid per page read - ~$0.0045/page in 2026)</li>
              <li>The ability to run Kindle Countdown Deals and Free Promotions</li>
              <li>Slightly higher discoverability inside Amazon&apos;s recommendation engine</li>
            </ul>
            <p className="text-muted-foreground">
              Going wide means publishing on Apple Books, Kobo, Google Play, and Barnes &amp; Noble too. You
              earn per-sale royalties on all of them, but you cannot be in Kindle Unlimited.
            </p>
            <p className="text-muted-foreground">
              The honest rule of thumb in 2026: <strong className="text-foreground">romance and KU-heavy
              genres should start in Select.</strong> Literary fiction, nonfiction, and anything with a strong
              international audience should go wide. Series authors should keep books 1–3 wide and put book 4+
              in Select to capture page-read income from binge-readers.
            </p>
          </section>

          <section id="launch-week">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              9. Launch-week strategy
            </h2>
            <p className="text-muted-foreground">
              Amazon&apos;s algorithm gives new releases a 30-day boost - but it weighs the first 7 days
              heaviest. A book that sells 200 copies in week one will outperform a book that sells the same 200
              copies spread over month one, every time. Your job in launch week is to concentrate.
            </p>
            <p className="text-muted-foreground">
              The standard playbook:
            </p>
            <ol className="list-decimal pl-6 space-y-2 text-muted-foreground">
              <li>Set the book to <strong className="text-foreground">pre-order</strong> 4–6 weeks before launch</li>
              <li>Email your existing list 7 days before launch with a pre-order link</li>
              <li>Email again on launch day with a 24-hour discount</li>
              <li>Run a price-pulse promotion on day 5 ($0.99 → $2.99 by day 8)</li>
              <li>Book a BookBub Featured Deal or Freebooksy ad to stack on top of the price-pulse</li>
              <li>Cross-promote with three to five authors in your subgenre</li>
            </ol>
          </section>

          <section id="after-launch">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mt-12 mb-4 tracking-tight">
              10. After launch: reviews, ads, and growth
            </h2>
            <p className="text-muted-foreground">
              The single thing that matters most after launch is reviews. Amazon now requires 50+ reviews
              before a book can appear in the &ldquo;Also Bought&rdquo; carousels in any meaningful volume.
              Below 50, you are effectively invisible. Above 50, every additional review compounds.
            </p>
            <p className="text-muted-foreground">
              The cleanest way to get reviews is to put a request at the end of your book - one line, with a
              clickable link to the review page on Amazon. Do not message reviewers. Do not buy reviews.
              Amazon&apos;s detection is good and the bans are permanent.
            </p>
            <p className="text-muted-foreground">
              For paid ads: Amazon Ads beats Facebook in 2026 for fiction. Start with a small $5/day budget on
              auto-targeting for the first 14 days, let Amazon learn your audience, then move to
              hand-selected keywords and competitor ASINs in week 3.
            </p>
          </section>

          <section id="faq" itemScope itemType="https://schema.org/FAQPage">
            <div className="text-center mb-10 mt-12">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-[#a78bfa] text-xs font-semibold tracking-wider uppercase mb-4">
                💬 Common Questions
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
                Frequently asked questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  q: 'How much does it cost to publish a book on Kindle?',
                  a: 'Publishing itself is free. KDP charges no upload, listing, or hosting fees. Realistic costs for a professional self-published novel: $300–$700 for a cover, $0–$1,500 for editing, and $50–$500 in launch-week advertising. Many authors publish for under $500 total their first time.',
                },
                {
                  q: 'How long does Amazon take to publish a book?',
                  a: 'Review typically takes 24–72 hours. Most books go live the same day or next day. New publisher accounts and first-time authors sometimes face an extra round of review (3–5 business days). Pre-orders can be set up to a year in advance.',
                },
                {
                  q: 'Do I keep the copyright to my book?',
                  a: 'Yes. Publishing on KDP grants Amazon a non-exclusive licence to sell your book. You retain full copyright. You can unpublish at any time, edit at any time, and republish elsewhere (unless you are in KDP Select for that 90-day window).',
                },
                {
                  q: 'Can I publish an AI-generated novel on Kindle?',
                  a: 'Yes, with disclosure. As of 2024, Amazon requires you to disclose AI-generated content at upload time. Authors using StoryMint typically select "AI-assisted" - meaning the underlying generation is AI but you have meaningfully edited the result. This is fully permitted and remains the most common indie workflow in 2026.',
                },
              ].map((item, i) => (
                <div
                  key={item.q}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                  className="group rounded-xl border border-border bg-card p-6 hover:border-primary/35 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <span
                      aria-hidden="true"
                      className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-primary/15 text-primary text-sm font-bold border border-primary/30"
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3
                        itemProp="name"
                        className="text-base sm:text-lg font-semibold text-foreground leading-snug mb-3 group-hover:text-primary transition-colors"
                      >
                        {item.q}
                      </h3>
                      <div
                        itemScope
                        itemProp="acceptedAnswer"
                        itemType="https://schema.org/Answer"
                        className="pl-4 border-l-2 border-primary/20"
                      >
                        <p
                          itemProp="text"
                          className="text-muted-foreground text-[15px] leading-[1.7]"
                        >
                          {item.a}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 p-8 rounded-xl border border-primary bg-primary/5 text-center">
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Ready to write the book you&apos;ll publish?
            </h2>
            <p className="text-muted-foreground mb-6">
              StoryMint generates a publication-ready novel from your premise - EPUB, DOCX, and cover included.
              Start with three free chapters, no credit card.
            </p>
            <Link
              to="/signup"
              className="inline-block px-8 py-3 bg-primary text-primary-foreground font-semibold rounded-md hover:bg-primary/90 transition-colors text-lg"
            >
              Start writing free →
            </Link>
          </section>
        </div>

        <footer className="mt-12 pt-8 border-t border-border">
          <Link
            to="/blog"
            className="text-sm font-semibold text-[#a78bfa] hover:text-foreground transition-colors"
          >
            ← Back to all posts
          </Link>
        </footer>
      </article>

      <Footer />
    </div>
  );
}
